import * as THREE from 'three';
import TWEEN from '@tweenjs/tween.js';
import { audioManager } from '@modules/audio/AudioManager.js';
import { showNotification } from '@modules/ui/NotificationManager.js';
import { SceneObjectsType } from '@modules/core/types.js';

/**
 * Manajemen Navigasi Komprehensif untuk Milkyway Explorer
 * Menggabungkan navigasi UI dan navigasi kamera 3D
 */
class NavigationManager {
  // Singleton instance
  static instance = null;

  /**
   * Dapatkan instance singleton NavigationManager
   * @returns {NavigationManager} Instance singleton
   */
  static getInstance() {
    if (!NavigationManager.instance) {
      NavigationManager.instance = new NavigationManager();
    }
    return NavigationManager.instance;
  }

  constructor() {
    if (NavigationManager.instance) {
      return NavigationManager.instance;
    }

    // Inisialisasi properti
    this.camera = null;
    this.controls = null;
    this.sceneObjects = null;
    this.uiControls = [];
    this.eventListeners = new Map();
    this.initialized = false;

    // Status navigasi
    this.targetPlanetToFollow = null;
    this.currentFocusTarget = null;
    this.currentPlanetIndex = -1;

    // Binding metode
    this.init = this.init.bind(this);
    this.focusOnObject = this.focusOnObject.bind(this);
    this.createButton = this.createButton.bind(this);
    this.handleNavigationAction = this.handleNavigationAction.bind(this);

    NavigationManager.instance = this;
  }

  /**
   * Inisialisasi NavigationManager
   * @param {Object} config - Konfigurasi inisialisasi
   * @param {THREE.Camera} config.camera - Kamera scene
   * @param {OrbitControls} config.controls - Kontrol orbit
   * @param {SceneObjectsType} config.sceneObjects - Objek-objek scene
   */
  async init({ camera, controls, sceneObjects }) {
    if (this.initialized) {
      console.warn('NavigationManager sudah diinisialisasi');
      return;
    }

    try {
      // Simpan referensi
      this.camera = camera;
      this.controls = controls;
      this.sceneObjects = sceneObjects;

      // Inisialisasi audio
      if (audioManager && typeof audioManager.init === 'function') {
        await audioManager.init();
      }

      this.initialized = true;
      console.log('NavigationManager diinisialisasi berhasil');
    } catch (error) {
      console.error('Gagal menginisialisasi NavigationManager:', error);
      throw error;
    }
  }

  /**
   * Fokus kamera pada objek tertentu
   * @param {THREE.Object3D} targetObject - Objek target
   * @param {THREE.Vector3} [offset=null] - Offset kamera
   * @throws {Error} Jika konfigurasi tidak lengkap atau fokus gagal
   */
  focusOnObject(targetObject, offset = null) {
    // Validasi input
    if (!targetObject) {
      const error = new Error('Objek target tidak valid');
      console.error(error);
      showNotification('Gagal fokus: Objek tidak valid', 'error');
      throw error;
    }

    // Cek konfigurasi
    if (!this.camera || !this.controls) {
      const error = new Error('Konfigurasi navigasi tidak lengkap');
      console.error(error);
      showNotification('Gagal fokus: Konfigurasi tidak lengkap', 'error');
      throw error;
    }

    try {
      // Log detail fokus untuk debugging
      console.debug(`Fokus pada objek: ${targetObject.name || 'Objek Tanpa Nama'}`);

      this.currentFocusTarget = targetObject;
      
      // Pastikan audioManager tersedia sebelum memutar suara
      if (this.audioManager?.playSound) {
        this.audioManager.playSound('click');
      }

      const targetPosition = new THREE.Vector3();
      targetObject.getWorldPosition(targetPosition);

      const distance = this._calculateFocusDistance(targetObject);
      this._updateNavigationStatus(targetObject);
      this._animateCameraTransition(targetPosition, distance, offset);

      return true; // Indikasi fokus berhasil
    } catch (error) {
      console.error('Kesalahan saat fokus objek:', error);
      showNotification('Gagal fokus objek', 'error');
      throw error; // Re-throw untuk penanganan lebih lanjut
    }
  }

  /**
   * Buat tombol navigasi
   * @param {Object} config - Konfigurasi tombol
   * @returns {HTMLButtonElement} Elemen tombol
   */
  createButton(config) {
    if (!config || typeof config !== 'object') {
      throw new Error('Konfigurasi tombol tidak valid');
    }

    const { name, symbol, title = name, action, callback } = config;

    if (!name || !symbol) {
      throw new Error('Nama dan simbol tombol diperlukan');
    }

    try {
      const button = document.createElement('button');
      button.className = `nav-button nav-${name}`;
      button.setAttribute('aria-label', title);
      button.setAttribute('role', 'button');
      button.tabIndex = 0;
      button.innerHTML = symbol;
      button.title = title;

      this._setupButtonEvents(button, config);
      
      // Simpan referensi kontrol UI
      this.uiControls.push({
        name,
        element: button,
        action
      });

      return button;
    } catch (error) {
      console.error('Kesalahan membuat tombol navigasi:', error);
      throw error;
    }
  }

  /**
   * Aktifkan/nonaktifkan tombol
   * @param {string} name - Nama tombol
   * @param {boolean} [enabled=true] - Status aktif
   */
  enableButton(name, enabled = true) {
    const control = this.uiControls.find(c => c.name === name);
    if (control && control.element) {
      control.element.disabled = !enabled;
    }
  }

  /**
   * Menghitung jarak fokus berdasarkan jenis objek
   * Metode ini menentukan jarak fokus kamera secara dinamis
   * berdasarkan tipe objek yang dituju
   * 
   * @private
   * @param {THREE.Object3D} targetObject - Objek target untuk fokus
   * @returns {number} Jarak fokus yang disesuaikan
   */
  _calculateFocusDistance(targetObject) {
    // Kasus khusus untuk matahari - fokus dari jarak jauh
    if (targetObject === this.sceneObjects?.sun) return 100;
    
    // Daftar objek dengan jarak dan notifikasi spesifik
    const objectTypes = [
      { 
        object: this.sceneObjects?.ufo, 
        distance: 10, 
        notification: 'UFO Terdeteksi! 👽' 
      },
      { 
        object: this.sceneObjects?.asteroidBelt, 
        distance: 50, 
        notification: 'Menjelajahi Sabuk Asteroid 💫' 
      },
      { 
        object: this.sceneObjects?.comet?.group, 
        distance: 20, 
        notification: 'Melacak Komet ☄️' 
      },
      { 
        object: this.sceneObjects?.spaceStation?.group, 
        distance: 15, 
        notification: 'Mengunjungi Stasiun Luar Angkasa 📡' 
      },
      { 
        object: this.sceneObjects?.rocket, 
        distance: 10, 
        notification: 'Melacak Peluncuran Roket 🚀' 
      }
    ];

    // Cari objek yang cocok dalam daftar
    const matchedType = objectTypes.find(type => targetObject === type.object);
    if (matchedType) {
      // Tampilkan notifikasi untuk objek khusus
      showNotification?.(matchedType.notification, 'info');
      return matchedType.distance;
    }

    // Default: Gunakan radius objek untuk menentukan jarak
    // Jika tidak ada radius, gunakan jarak default 30
    return targetObject?.userData?.radius 
      ? targetObject.userData.radius * 5 
      : 30;
  }

  /**
   * Memperbarui status navigasi berdasarkan objek target
   * @private
   * @param {THREE.Object3D} targetObject - Objek target
   */
  _updateNavigationStatus(targetObject) {
    this.targetPlanetToFollow = null;
    this.currentPlanetIndex = -1;

    // Cek planet
    const planetIndex = this.sceneObjects.planets.findIndex(
      planet => targetObject === planet.group || targetObject === planet.mesh
    );
    
    if (planetIndex !== -1) {
      const planet = this.sceneObjects.planets[planetIndex];
      this.targetPlanetToFollow = planet;
      this.currentPlanetIndex = planetIndex;
      showNotification(`Fokus pada ${planet.name}`, 'info');
    }

    // Cek bulan
    const moonIndex = this.sceneObjects.moons.findIndex(
      moon => targetObject === moon.group
    );

    if (moonIndex !== -1) {
      const moon = this.sceneObjects.moons[moonIndex];
      showNotification(`Menjelajahi Bulan: ${moon.name}`, 'info');
    }

    // Kasus khusus matahari
    if (targetObject === this.sceneObjects.sun) {
      showNotification('Fokus pada Matahari', 'info');
    }
  }

  /**
   * Animasi transisi kamera dengan TWEEN
   * @private
   * @param {THREE.Vector3} targetPosition - Posisi target
   * @param {number} distance - Jarak fokus
   * @param {THREE.Vector3} [offset=null] - Offset kamera
   */
  _animateCameraTransition(targetPosition, distance, offset = null) {
    TWEEN.removeAll();

    // Animasi target kontrol
    new TWEEN.Tween(this.controls.target)
      .to(targetPosition, 1500)
      .easing(TWEEN.Easing.Exponential.Out)
      .start();

    // Hitung offset kamera
    const cameraOffset = offset 
      ? offset.clone().normalize().multiplyScalar(distance)
      : new THREE.Vector3(0, 0.3, 1).normalize().multiplyScalar(distance);

    const offsetPosition = targetPosition.clone().add(cameraOffset);

    // Animasi posisi kamera
    new TWEEN.Tween(this.camera.position)
      .to(offsetPosition, 1800)
      .easing(TWEEN.Easing.Exponential.Out)
      .delay(100)
      .onUpdate(() => this.controls.update())
      .start();
  }

  /**
   * Tangani aksi navigasi
   * @param {string} action - Aksi navigasi
   */
  handleNavigationAction(action) {
    switch (action) {
      case 'focus-moon':
        this._focusOnMoon();
        break;
      case 'focus-iss':
        this._focusOnSpaceStation();
        break;
      case 'reset-camera':
        this._resetCamera();
        break;
      default:
        console.warn('Aksi navigasi tidak dikenali:', action);
    }
  }

  /**
   * Fokus pada bulan bumi
   * @private
   */
  _focusOnMoon() {
    const earthMoon = this.sceneObjects.moons[0];
    if (earthMoon?.group) {
      this.focusOnObject(earthMoon.group, new THREE.Vector3(5, 3, 5));
    } else {
      showNotification('Bulan tidak ditemukan', 'error');
    }
  }

  /**
   * Fokus pada stasiun luar angkasa
   * @private
   */
  _focusOnSpaceStation() {
    if (this.sceneObjects.spaceStation?.group) {
      this.focusOnObject(this.sceneObjects.spaceStation.group, new THREE.Vector3(3, 2, 3));
    } else {
      showNotification('Stasiun Luar Angkasa tidak ditemukan', 'error');
    }
  }

  /**
   * Mereset kamera ke tampilan awal
   * @private
   */
  _resetCamera() {
    new TWEEN.Tween(this.camera.position)
      .to({ x: 0, y: 80, z: 200 }, 1000)
      .easing(TWEEN.Easing.Cubic.InOut)
      .start();

    new TWEEN.Tween(this.controls.target)
      .to({ x: 0, y: 0, z: 0 }, 1000)
      .easing(TWEEN.Easing.Cubic.InOut)
      .start();

    // Reset status navigasi
    this.targetPlanetToFollow = null;
    this.currentFocusTarget = null;
    this.currentPlanetIndex = -1;

    // Atur ulang kontrol
    this.controls.autoRotate = false;
    this.controls.enableRotate = true;
    this.controls.enableZoom = true;
    this.controls.enablePan = true;
    this.controls.update();

    showNotification('Tampilan direset ke tinjauan sistem solar', 'info');
    audioManager.playSound('click');
  }

  /**
   * Bersihkan event listener dan reset
   */
  cleanup() {
    // Hapus event listener
    this.eventListeners.forEach((listeners, element) => {
      listeners.forEach(({ type, handler }) => {
        element.removeEventListener(type, handler);
      });
    });

    // Reset properti
    this.eventListeners.clear();
    this.uiControls = [];
    this.initialized = false;
    this.camera = null;
    this.controls = null;
    this.sceneObjects = null;
  }
}

// Buat instance singleton
export const navigationManager = NavigationManager.getInstance();
export default navigationManager; 